import {StateController} from "@lit-app/state/src/state-controller";
import {html, HTMLTemplateResult, nothing, PropertyValues} from "lit";
import {state} from "lit/decorators/state.js";
import {classMap} from "lit/directives/class-map.js";
import {repeat} from "lit/directives/repeat.js";
import {styleMap} from "lit/directives/style-map.js";
import {DateTime, Interval} from "luxon";
import {debounceTime, distinctUntilChanged, filter, skip, Subject, take, takeUntil} from "rxjs";
import {container} from "tsyringe";
import type {StickyEntity} from "../app.state";
import {appState} from "../app.state";
import {AbstractCommand} from "../commands/abstract.command";
import {ICommand} from "../commands/i-command.interface";
import {AbstractLitElement} from "../components/abstract-lit.element";
import type {PopoverListItem} from "@o2d/popover-list";
import {get} from "lit-translate";
import {CALENDAR_PROJECT_UID, ISSUE_TYPE_MILESTONE} from "../constants";
import {belongsToRoot} from "../domain/helpers/issue-type-root.helper";
import {FiltersDTO} from "../domain/dtos/filters.dto";
import {Entity} from "../domain/entities/entity";
import {IssueType} from "../domain/entities/issue-type.entity";
import {Issue} from "../domain/entities/issue.entity";
import {IssueHelper} from "../domain/helpers/issue.helper";
import {IssueRepository} from "../domain/repositories/issue.repository";
import {ViewMode} from "../enums/view-mode.enum";
import {ArrayHelper} from "../helpers/array.helper";
import {FilterHelper} from "../helpers/filter.helper";
import {HtmlHelper} from "../helpers/html.helper";
import {ObjectHelper} from "../helpers/object.helper";
import {StringHelper} from "../helpers/string.helper";
import {ToolbarHelper} from "../helpers/toolbar.helper";
import {UiHelper} from "../helpers/ui.helper";
import {BaseMessage} from "../message-broker/base-message.interface";
import {MessageBroker} from "../message-broker/message-broker";
import {EntityChangedPayload} from "../message-broker/payloads/entity-changed.payload";
import {EntityManager} from "../persistence/entity.manager";
import {CommandBus} from "../services/command-bus.service";
import {IntervalLoadingGuard} from "../services/interval-loading.guard";
import {TimelineAddIssueTrackingService} from "../services/timeline-add-issue-tracking.service";
import {TimelineMovingDraggingService} from "../services/timeline-moving-dragging.service";
import {TimelineLayoutService, TimelineRequestManager} from "../services";
import {CalendarViewCalculator} from "../timeline/calendar-view-calculator";
import {TimelineBackendConnector} from "../timeline/timeline-backend.connector";
import {TimelineConfiguration} from "../timeline/timeline-configuration.interface";
import {TimelineRowHeader} from "../timeline/timeline-row-header.interface";
import {TimelineLayout, TimelineLayoutConfig} from "../timeline/timeline-layout.types";
import {ClassOf} from "../types/class-of.type";
import {ListenerInfo} from "../message-broker/listener-info.interface";
import {DraggingCoordinates, TouchService} from "../services/touch.service";
import {map} from "lit/directives/map.js";
import {when} from "lit/directives/when.js";
import {TimelineConfigurationManager} from "../services/timeline-configuration.manager";
import {AuthorizationService} from "../services/authorization.service";
import {CommandName} from "../commands/command-name.enum";
import {ExecutionVerification} from "../services/execution-verification.type";
import {PayloadOfDayAndRow} from "../types/payload-of-day-and-row.type";
import {IssueMatrix} from "../types/issue-matrix.type";
import {CellInfo} from "../types/cell-info.type";
import {EntityCreatedPayload} from "../message-broker/payloads/entity-created.payload";
import {TimelineToolbarItem} from "../types/timeline-toolbar-item.interface";
import {issueFiltersToolbarItem} from "../toolbar-items/o2d-issue-filters.toolbar-item";
import {resetToTodayToolbarItem} from "../toolbar-items/o2d-reset-to-today.toolbar-item";
import {compactViewToolbarItem, detailViewToolbarItem, standardViewToolbarItem} from "../toolbar-items/o2d-cell-width-view.toolbar-items";
import {addEntityMenuToolbarItem} from "../toolbar-items/o2d-add-entity-menu.toolbar-item";
import {Unsubscribe} from "@lit-app/state/src/state";

export abstract class AbstractHaTimelineView extends AbstractLitElement {

    @state()
    protected _timelineConfigurations: TimelineConfiguration[] = [];
    protected _listeners = new Map<string, ListenerInfo<any>>([
        [`ifIssueCreatedThenRefreshDays`, <Omit<ListenerInfo<any>, 'disconnect'>>{
            messageType: `entity.created`,
            bufferTime: 250,
            filterFn: message => message.payload.prototype === Issue,
            method: messages => {
                const intervals = messages.map(message => message.payload.entity.timeRange);
                this._refreshTimelineByIntervals(intervals).then();
            }
        }],
        [`ifIssueTimeRangeChangedThenRefreshDays`, <Omit<ListenerInfo<any>, 'disconnect'>>{
            messageType: `entity.changed`,
            bufferTime: 250,
            filterFn: message => message.payload.prototype === Issue,
            method: async messages => {
                const issueRepository = container.resolve(IssueRepository);
                const intervals = await Promise.all(messages.map(async (message) => {
                    if (`timeRange` in message.payload.changeSet) {
                        const current = message.payload.changeSet[`timeRange`].current as Interval;
                        const last = message.payload.changeSet[`timeRange`].last as Interval;
                        return [current, last];
                    }

                    const issue = (await issueRepository.findOneById(message.payload.uid))!;
                    return [issue.timeRange];
                }));

                await this._refreshTimelineByIntervals(intervals.flat(1));
            }
        }],
        [`ifIssueDeletedThenRefreshDays`, {
            messageType: `entity.deleted`,
            method: async messages => {
                const timeRangesOfDeletedIssues = messages.map(message => (message.payload.entity as Issue).timeRange);
                await this._refreshTimelineByIntervals(timeRangesOfDeletedIssues);
            },
            bufferTime: 250,
            filterFn: message => message.payload.entity instanceof Issue,
        }],
        [`ifEntityOfTimelineHeaderDeletedThenRefreshTimelineHeaders`, {
            messageType: `entity.deleted`,
            method: this._onTimelineHeaderEntityDeleted,
            filterFn: message => this._timelineConfigurations.some(c => c.baseEntity === message.payload.entity.constructor)
        }],
        [`ifEntityOfTimelineHeaderChangedThenRefreshTimelineHeaders`, {
            messageType: `entity.changed`,
            method: this._onEntityChangedOrCreated,
            filterFn: message => {
                return this._timelineConfigurations.some(c => c.baseEntity === message.payload.prototype)
            },
        }],
        [`ifEntityOfTimelineHeaderCreatedThenRefreshTimelineHeaders`, {
            messageType: `entity.created`,
            method: this._onEntityChangedOrCreated,
            filterFn: (message: BaseMessage<EntityCreatedPayload<Entity>>) => this._timelineConfigurations.some(c => c.baseEntity === message.payload.prototype),
        }],
        [`ifIssueTypeChangedThenRefreshDaysWithIssuesOfChangedType`, {
            messageType: `entity.changed`,
            method: this._onIssueTypeChanged,
            filterFn: message => {
                if (message.payload.prototype !== IssueType) {
                    return false;
                }

                return ArrayHelper.intersect(
                    Array.from<keyof IssueType>(Object.keys(message.payload.changeSet) as Array<keyof IssueType>), <Array<keyof IssueType>>[`color`, `shortcut`]
                ).length > 0
            },
        }]
    ]);
    @state()
    protected _highlightedRows: TimelineRowHeader<Entity>[] = [];
    @state()
    protected _timelineRowHeadersInView: TimelineRowHeader<Entity>[] = [];
    protected _authorizationService = container.resolve(AuthorizationService);
    @state()
    protected _rowCursor: number = 0;
    protected _pointerDraggingActive: boolean = false;
    @state()
    protected _timelineRowHeaders: TimelineRowHeader<Entity>[] = [];
    @state()
    protected _stickyTimelineRowHeaders: TimelineRowHeader<Entity>[] = [];
    @state()
    protected _filteredTimelineRowHeaders: TimelineRowHeader<Entity>[] = [];
    protected _numberOfRows: number = 0;
    protected _timelineCells = new Map<number, Map<string, Map<string, PayloadOfDayAndRow>>>();
    protected _filteredOutIssues: string[] = [];
    private _initialRowCursorSet: boolean = false;
    // Zwischenspeicher für Issues die vor Headers ankommen (Quick-Fix für Race Condition)
    private _pendingIssuesForClassification?: Map<number, Map<string, Issue>>;
    private _calendarViewCalculator: CalendarViewCalculator | null = null;
    private _cellHeight: number = 0;
    private _cellWidth: number = 0;

    // Timeline Layout Service Integration
    private _timelineLayoutService!: TimelineLayoutService;
    private _inPopoverVisibleIssueElement: (HTMLElement & { issue: Issue }) | undefined;
    @state()
    private _isOpenedAddEntitySubMenu = false;
    @state()
    private _precompiledFilters: ((issue: Issue) => boolean) | undefined;

    /**
     * Uid set the activity filter (changedBy/changedWithin) last resolved to — the
     * second parameter FilterHelper.precompileFilters needs to gray out non-matches
     * (plan 7.5). `null` while the filter is inactive, or while a fetch for it has not
     * resolved yet (there is a short, accepted window where nothing is grayed out).
     */
    private _matchedUuids: Set<string> | null = null;

    /**
     * Guards the async uid-set fetch against a stale response overwriting a NEWER
     * selection's result (7.5, point 4) — mirrors AbstractStandardTimelineView's own
     * field of the same name and purpose.
     */
    private _matchedUuidsFetchGeneration = 0;
    private _resizeObserver: ResizeObserver | null = null;
    private _resizeSubject = new Subject<ResizeObserverEntry>();
    private _resizeObserver$ = this._resizeSubject.pipe(debounceTime(150));
    @state()
    private _viewMode: ViewMode[] = [ViewMode.DEFAULT];
    private _loadingTimelineRowHeadersPromise: Promise<void> | null = null;
    private _toolbarItems: TimelineToolbarItem[] = [
        issueFiltersToolbarItem,
        resetToTodayToolbarItem,
        compactViewToolbarItem,
        standardViewToolbarItem,
        detailViewToolbarItem,
        addEntityMenuToolbarItem,
    ];
    private readonly _appStateUnsubscriber: Unsubscribe;

    public constructor() {
        super();
        new StateController(this, appState);

        // Migrate legacy sticky entities data to new groupKey structure
        appState.migrateLegacyStickyTimelineEntities();

        // Inject Timeline Layout Service
        this._timelineLayoutService = container.resolve(TimelineLayoutService);

        this._appStateUnsubscriber = appState.subscribe(key => {
            if (key === 'dateCursor') {
                // Timeline-Navigation: alle Operationen aus _resetToToday hierher verschoben
                this._interval = null;

                // IMPORTANT: Layout muss nach Datumsänderung neu berechnet werden!
                this._layout = null;

                // Skip automatic refresh during drag operations - use TimelineRequestManager instead
                if (!this._viewInDraggingMode && !this._pointerDraggingActive) {
                    this._refreshTimelineByIntervals([this.interval]).then();
                }

                // Don't reset rowCursor during dragging (Space-Key or Pointer dragging)
                if (!this._viewInDraggingMode && !this._pointerDraggingActive &&
                    typeof this._timelineConfigurations[0]?.initialRowIndexFn === "function" &&
                    this._filteredTimelineRowHeaders.length > 0) {
                    // Use filtered rows (without sticky) for initial index calculation
                    const initialCursor = this._timelineConfigurations[0].initialRowIndexFn(this._filteredTimelineRowHeaders);
                    // Adjust cursor to ensure full screen usage
                    this._rowCursor = this._adjustRowCursorForFullScreen(initialCursor);
                }

                // Force UI update nach Layout-Invalidierung
                this.requestUpdate();
            }
            if (key === 'cellWidthMultiplication') {
                // Week/Day View Toggle: View neu berechnen wenn sich die Zellbreite ändert
                this._reCalculateView();
            }
        });
    }

    private _layout: TimelineLayout | null = null;

    /**
     * Get current timeline layout from service
     * Lazy-initializes layout if not yet calculated
     */
    protected get layout(): TimelineLayout {
        if (!this._layout) {
            const newLayout = this._timelineLayoutService.calculateLayout(this._createLayoutConfig());
            this._layout = newLayout;
            // Also update the service's cache for change detection
            this._timelineLayoutService.cacheLayout(newLayout);
        }
        return this._layout;
    }

    protected abstract get _tags(): Array<string[]>;

    protected _interval: Interval | null = null;

    protected get interval(): Interval {
        if (!this._interval) {
            this._interval = Interval.fromDateTimes(
                this.firstDayOfCalendar,
                this.lastDayOfCalendar
            );
        }

        return this._interval!;
    }

    protected set interval(interval: Interval | null) {
        if (interval === this._interval) {
            return;
        }

        if (!!interval && !!this._interval && interval.equals(this._interval)) {
            return;
        }

        this._interval = interval;
    }

    protected get firstDayOfCalendar(): DateTime {
        return this.layout.firstDayOfCalendar;
    }

    protected get lastDayOfCalendar(): DateTime {
        return this.layout.lastDayOfCalendar;
    }

    protected _daysBefore: number = 5;

    protected get daysBefore(): number {
        return this._daysBefore;
    }

    private get _hasActiveFilters(): boolean {
        return appState.hasAppliedFilters();
    }

    private _daysAfter: number = -1;

    private get daysAfter(): number {
        if (this._daysAfter === -1) {
            // Use layout service for consistent calculation
            return this.layout.daysAfter;
        }

        return this._daysAfter;
    }

    private set daysAfter(daysAfter: number) {
        if (daysAfter === this._daysAfter) {
            return;
        }
        this._daysAfter = daysAfter;
    }

    private get _viewInDefaultMode(): boolean {
        return ArrayHelper.first(this._viewMode) === ViewMode.DEFAULT;
    }

    private get _viewInDraggingMode(): boolean {
        return ArrayHelper.first(this._viewMode) === ViewMode.DRAGGING;
    }

    private get _viewInIssueAddMode(): boolean {
        return ArrayHelper.first(this._viewMode) === ViewMode.ISSUE_ADD;
    }

    public setTimelineConfiguration(...timelineConfigurations: TimelineConfiguration[]): void {
        this._timelineConfigurations = [...timelineConfigurations];
        this._loadTimelineRowHeaders().then();
    }

    public addTimelineConfiguration(...timelineConfigurations: TimelineConfiguration[]): void {
        this._timelineConfigurations = [
            ...this._timelineConfigurations,
            ...timelineConfigurations
        ];
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this._ensureSystemStickyEntities();

        HtmlHelper.setGlobalVar(`remainder-x`, `0px`);
        HtmlHelper.setGlobalVar(`remainder-y-top`, `0px`);

        const configurations = container
            .resolve(TimelineConfigurationManager)
            .get(StringHelper.toKebabCase(this.constructor.name));

        if (configurations.length > 0) {
            this.addTimelineConfiguration(...configurations);
        }

        this.classList.add(`ha-timeline-container`);

        container.resolve(TimelineAddIssueTrackingService)
            .register(this)
            .pipe(
                takeUntil(this._disconnected$),
                distinctUntilChanged((previous, current) => {
                    return previous.ctrlPressed === current.ctrlPressed && previous.mouseOver === current.mouseOver
                })
            )
            .subscribe(
                trackingInfo => {
                    if (!trackingInfo.mouseOver || !trackingInfo.ctrlPressed) {
                        const idx = [...this._viewMode].findIndex(viewMode => viewMode === ViewMode.ISSUE_ADD);
                        if (idx > -1) {
                            this._viewMode = [...this._viewMode.slice(0, idx), ...this._viewMode.slice(idx + 1)];
                        }
                    }

                    if (trackingInfo.ctrlPressed && !!trackingInfo.mouseOver && !this._viewMode.includes(ViewMode.ISSUE_ADD)) {
                        this._viewMode = [ViewMode.ISSUE_ADD, ...this._viewMode] /*this.classList.add(`issue-add-mode`)*/
                    }
                }
            );

        this._resizeObserver = new ResizeObserver(([entry]) => this._resizeSubject.next(entry));

        // Handle initial size for rowCursor adjustment
        this._resizeObserver$.pipe(
            take(1), // Only take the first resize event for initial adjustment
            filter(() => !this._initialRowCursorSet), // Only if not already set
            debounceTime(100) // Small debounce for initial render
        ).subscribe(() => {
            console.debug(`Initial row cursor adjustment`);

            // Recalculate view to get the proper numberOfRows
            this._reCalculateView();

            // Now adjust the initial row cursor with proper numberOfRows
            if (this._numberOfRows > 0 && this._filteredTimelineRowHeaders.length > 0) {
                const initialCursor = this._timelineConfigurations.map(configuration => {
                    if (!configuration.initialRowIndexFn) {
                        return 0;
                    }
                    return configuration.initialRowIndexFn(this._filteredTimelineRowHeaders);
                }).reduce((previousValue, currentValue) => previousValue + currentValue, 0);

                this._rowCursor = this._adjustRowCursorForFullScreen(initialCursor);
                this._initialRowCursorSet = true;
            }
        });

        // Regular resize handling (skip first to avoid double processing)
        this._resizeObserver$.pipe(
            skip(1),
            debounceTime(400)
        ).subscribe(() => this._reCalculateView());

        this._resizeObserver.observe(this as HTMLElement);

        const messageBroker = container.resolve(MessageBroker);
        for (const listener of this._listeners.values()) {
            listener.disconnect = this._disconnected$;
            messageBroker.registerListener(this, listener);
        }

        appState.listen$(`filters`).pipe(
            takeUntil(this._disconnected$),
        ).subscribe(filters => this._precompileFilters({...filters}));

        this._precompileFilters({...appState.filters});

        appState.listen$(`stickyTimelineEntities`).pipe(
            takeUntil(this._disconnected$)
        ).subscribe(() => {
            this._loadTimelineRowHeaders().then();
        });

        this._timelineConfigurations.forEach(timeline => {
            if (typeof timeline.onConnection === 'function') {
                timeline.onConnection.call(this as AbstractHaTimelineView);
            }
        });
    }

    public disconnectedCallback() {
        this._resizeObserver?.disconnect();
        container.resolve(TimelineMovingDraggingService).unregister();
        this._disconnected$.next();
        this._disconnected$.complete();
        this._appStateUnsubscriber();
        super.disconnectedCallback();
    }

    protected rowSortingFn: (rowHeaderA: TimelineRowHeader<Entity>, rowHeaderB: TimelineRowHeader<Entity>) => number = (rowHeaderA: TimelineRowHeader<Entity>, rowHeaderB: TimelineRowHeader<Entity>): number => {
        return rowHeaderA.title.localeCompare(rowHeaderB.title);
    };

    /**
     * Template Method to determine if this view uses fixed grid rows (repeat auto-fill)
     * or auto grid rows (CSS auto). Views with fixed grid rows need different Y-scroll behavior.
     *
     * @returns true for views with CSS "repeat(auto-fill, var(--cell-height))" like BoardsView
     * @returns false for views with CSS "auto" (default for most views)
     */
    protected _usesFixedGridRows(): boolean {
        return false; // Default: auto grid rows (most views)
    }

    protected render(): unknown {
        return html`
            <div class="view__body">
                <div class="view__column">
                    ${ToolbarHelper.renderTimelineToolbar(this._toolbarItems)}
                    ${this._renderGrid()}
                </div>
                <div class="sidebar__filter ${appState.todoFilterOpen ? 'expanded' : 'collapsed'}">
                    <main class="sidebar__filter__main">
                        <ha-form-issue-filters class="wrapper"></ha-form-issue-filters>
                    </main>
                </div>
            </div>
        `;
    }

    protected async firstUpdated(_changedProperties: PropertyValues): Promise<void> {

        // If _loadTimelineRowHeaders is already running (from connectedCallback subscription), wait for it
        if (this._loadingTimelineRowHeadersPromise) {
            await this._loadingTimelineRowHeadersPromise;
        }

        await this._loadTimelineRowHeaders();

        // Wait until headers are really loaded
        while (this._loadingTimelineRowHeadersPromise) {
            await this._loadingTimelineRowHeadersPromise;
        }

        // Set initial row cursor to 0 - proper adjustment will happen via ResizeObserver
        // when the element has valid dimensions
        this._rowCursor = 0;

        // das ist individuell → das muss ausgelagert und überschreibbar sein
        //await this._loadAndClassifyIssues();
        await this._refreshTimelineByIntervals([this.interval]);

        this._registerDragging();
    }

    protected willUpdate(_changedProperties: PropertyValues) {
        // Use layout from a service instead of individual calculations
        const currentLayout = this.layout;

        this._calendarViewCalculator = new CalendarViewCalculator(this.interval, appState.dateCursorDate);

        // Update cell dimensions from layout
        this._cellHeight = currentLayout.cellHeight;
        this._cellWidth = currentLayout.cellWidth;
        this._numberOfRows = currentLayout.numberOfRows;

        // Update row view using layout dimensions
        // Sticky rows kommen immer zuerst, dann die normalen scrollbaren rows
        const stickyCount = this._stickyTimelineRowHeaders.length;
        const availableRowsForScrollable = Math.max(0, currentLayout.numberOfRows - stickyCount);

        const scrollableRows = this._filteredTimelineRowHeaders.slice(
            this._rowCursor,
            this._rowCursor + availableRowsForScrollable
        );

        // Kombinierte View: Sticky rows + scrollable rows
        this._timelineRowHeadersInView = [
            ...this._stickyTimelineRowHeaders,
            ...scrollableRows
        ];
    }

    protected async _onClickRowHeaderContextMenu(rowHeader: TimelineRowHeader<Entity>, evt: MouseEvent): Promise<void> {
        const entityManager = container.resolve(EntityManager);

        const entityRepository = entityManager.getRepositoryForEntity(rowHeader.prototype);
        if (!entityRepository) {
            return;
        }

        const entity = await entityRepository.findOneById(rowHeader.uid);
        if (!entity) {
            return;
        }

        const selectedMenuItem = await UiHelper.showContextMenu(evt.currentTarget as HTMLElement, this._buildRowBasedContextMenu(entity, rowHeader.link));

        switch (selectedMenuItem?.id) {
            case `open-link`:
                UiHelper.openExternalLink(rowHeader.link);
                break;
            case `new-issue`:
                await this._onClickRowHeaderContextMenuNewIssue(entity)
                break;
            case `edit`:
                this._onClickRowHeaderContextMenuEdit(entity);
                break;
            case `delete`:
                this._onClickRowHeaderContextMenuDelete(entity);
                break;
            case `pin`:
                this._onClickRowHeaderContextMenuPin(entity.uid);
                break;
            case `unpin`:
                this._onClickRowHeaderContextMenuUnpin(entity.uid);
                break;
            case `make-sticky`:
                this._onClickRowHeaderContextMenuMakeSticky(entity);
                break;
            case `unsticky`:
                this._onClickRowHeaderContextMenuUnsticky(entity.uid);
                break;
            default:
                if (!!selectedMenuItem?.label) {
                    throw new Error(`Unknown context menu item selected: »${selectedMenuItem?.label}«`);
                }
        }
    }

    protected _renderHeaderCells(): unknown {
        return repeat(
            this._timelineRowHeadersInView,
            rowHeader => rowHeader.uid,
            (rowHeader: TimelineRowHeader<Entity>, index) => {
                const rowIndex = index + 5;
                const entityStyles = {
                    gridRowStart: rowIndex,
                    gridRowEnd: rowIndex,
                    gridColumnStart: 1,
                    gridColumnEnd: 1,
                    backgroundColor: rowHeader.color ?? `var(--row__header-background-color)`
                };

                const entityClasses = {
                    "row__header": true,
                    "row__highlighted": this._highlightedRows.includes(rowHeader),
                    "row__sticky": this._supportsStickyEntities() && appState.isStickyEntity(rowHeader.uid)
                }

                return html`
                    <div class=${classMap(entityClasses)}
                         style=${styleMap(entityStyles)}
                    >
                        <div class="row__title">
                            <div class="row__title-content">
                                <span>${rowHeader.title}</span>
                                ${this._supportsStickyEntities() && appState.isUserStickyEntity(rowHeader.uid) ? html`
                                    <sup class="row__sticky-badge"
                                         @click=${(e: MouseEvent) => this._onClickUnsticky(e, rowHeader.uid)}
                                         title="Sticky entfernen">
                                        <i class="icon icon-thumbtack"></i>
                                    </sup>
                                ` : nothing}
                            </div>
                        </div>
                        <div class="cursor"
                             @click=${() => this._toggleHighlightRow(rowHeader)}
                        >
                            <i class="icon icon-highlighter"></i>
                        </div>
                        <div class="cursor"
                             @click=${(evt: MouseEvent) => this._onClickRowHeaderContextMenu(rowHeader, evt)}
                        ><i class="icon icon-ellipsis-vertical"></i></div>
                    </div>
                `
            }
        );
    }

    protected _toggleHighlightRow(rowHeader: TimelineRowHeader<Entity>) {
        this._highlightedRows = this._highlightedRows.includes(rowHeader)
            ? this._highlightedRows.filter(highlightedRowHeader => highlightedRowHeader !== rowHeader)
            : [...this._highlightedRows, rowHeader];
    }

    protected _buildRowBasedContextMenu(entity: Entity, link: string | null | undefined): PopoverListItem[] {
        const menuItems: PopoverListItem[] = [
            {
                label: get(`ha-boards-view.row-title.open-link`),
                payload: entity,
                type: "item",
                id: `open-link`,
                disabled: !link
            },
            {
                label: get(`ha-boards-view.row-title.new-issue`),
                payload: entity,
                type: "item",
                id: `new-issue`,
                disabled: !this._authorizationService.hasPrivilege(`create.${StringHelper.toKebabCase(entity.constructor.name)}`)
            },
            {
                label: get(`ha-boards-view.row-title.edit`),
                payload: entity,
                type: "item",
                id: `edit`,
                disabled: !this._authorizationService.hasPrivilege(`update.${StringHelper.toKebabCase(entity.constructor.name)}`)
            }, {
                label: get(`ha-boards-view.row-title.delete`),
                payload: entity,
                type: "item",
                id: `delete`,
                disabled: !this._authorizationService.hasPrivilege(`delete.${StringHelper.toKebabCase(entity.constructor.name)}`)
            }, {
                label: appState.isEntityPinnedToUserView(this, entity.uid) ? get(`ha-boards-view.row-title.unpin-to-my-view`) : get(`ha-boards-view.row-title.pin-to-my-view`),
                payload: entity,
                type: "item",
                id: appState.isEntityPinnedToUserView(this, entity.uid) ? `unpin` : `pin`
            }
        ];

        // Sticky/Unsticky nur für Non-System-Sticky Entities hinzufügen - und nur wenn View sticky entities unterstützt
        if (this._supportsStickyEntities() && !appState.isSystemStickyEntity(entity.uid)) {
            menuItems.push({
                label: appState.isUserStickyEntity(entity.uid) ? get(`ha-boards-view.row-title.unsticky`) : get(`ha-boards-view.row-title.make-sticky`),
                payload: entity,
                type: "item",
                id: appState.isUserStickyEntity(entity.uid) ? `unsticky` : `make-sticky`,
                disabled: !appState.isUserStickyEntity(entity.uid) && appState.isUserStickyLimitReached()
            });
        }

        return menuItems;
    }

    protected async _loadTimelineRowHeaders(): Promise<void> {
        if (this._loadingTimelineRowHeadersPromise) {
            return;
        }

        // Create a comprehensive promise that covers the entire loading process
        this._loadingTimelineRowHeadersPromise = this._performCompleteHeaderLoad();
    }

    protected onTimelineRowHeadersUpdated(timelineRowHeaders: TimelineRowHeader<Entity>[]): TimelineRowHeader<Entity>[] {
        return timelineRowHeaders;
    }

    /**
     * Determines if this view supports sticky entities.
     * Override in subclasses to disable sticky entities (e.g., Resource Planning View).
     */
    protected _supportsStickyEntities(): boolean {
        return true;
    }

    /**
     * Creates TimelineConfigurations for sticky entities based on stored configuration data.
     * Returns configurations with customIssueQuery to load only the specified UIDs.
     */
    protected _createStickyTimelineConfigurations(): TimelineConfiguration[] {
        const entityManager = container.resolve(EntityManager);
        const configurations: TimelineConfiguration[] = [];

        // Group ALL sticky entities by baseEntity + baseEntityAlias
        const entitiesByConfig = new Map<string, StickyEntity[]>();

        for (const entity of appState.stickyTimelineEntities) {
            // Create unique key: baseEntity + baseEntityAlias
            const configKey = `${entity.baseEntity}_${entity.timelineConfig.baseEntityAlias || 'default'}`;
            if (!entitiesByConfig.has(configKey)) {
                entitiesByConfig.set(configKey, []);
            }
            entitiesByConfig.get(configKey)!.push(entity);
        }

        // Create configuration for each baseEntity+alias group
        for (const [configKey, entities] of entitiesByConfig) {
            const baseEntityName = entities[0].baseEntity;

            // Get entity class
            const kebabCaseName = StringHelper.toKebabCase(baseEntityName);
            const repository = entityManager.getRepositoryForEntityByName(kebabCaseName);
            const entityClass = repository?.entity;

            if (!entityClass) {
                console.warn(`[StickyEntities] Unknown entity: ${baseEntityName}`);
                continue;
            }

            // Use first entity's timelineConfig as template
            const timelineConfig = entities[0].timelineConfig;
            if (!timelineConfig) {
                console.warn(`[StickyEntities] No timelineConfig for ${baseEntityName}, skipping`);
                continue;
            }

            // Try to find matching view configuration for bgColorFn
            const viewConfig = this._timelineConfigurations.find(config =>
                config.baseEntity === entityClass
            );

            const uids = entities.map(entity => entity.uid);

            // Repository.entity is typed with the abstract base, but it holds the class given to
            // @persistence({entity}), which PersistenceDecoratorOptions types as the concrete
            // ClassOf<Entity>.
            const stickyConfig = {
                baseEntity: entityClass as ClassOf<Entity>,
                pathToUid: timelineConfig.pathToUid,
                pathToTitle: timelineConfig.pathToTitle,
                pathToEntity: timelineConfig.pathToEntity,
                baseEntityAlias: timelineConfig.baseEntityAlias, // Use original entity config
                rowFilterUids: uids,
                bgColorFn: viewConfig?.bgColorFn || (async () => `var(--row__header-background-color)`)
            };

            configurations.push(stickyConfig);
        }

        return configurations;
    }

    protected _classifyAndAssignIssuesToCells(issuesByDay: Map<number, Map<string, Issue>>): void {
        if (this._timelineRowHeaders.length === 0) {
            this._pendingIssuesForClassification = issuesByDay;
            return;
        }
        for (const [unixDateTime, issues] of issuesByDay) {
            if (!this._timelineCells.has(unixDateTime)) {
                this._timelineCells.set(unixDateTime, new Map<string, Map<string, PayloadOfDayAndRow>>);
            }

            const issuesByRowAndDay = this._timelineCells.get(unixDateTime)!;

            for (const [issueUid, issue] of issues) {
                for (const timelineRowHeader of this._timelineRowHeaders) {
                    const {configuration} = timelineRowHeader;
                    const prefix = StringHelper.buildUserViewConfigurationKey(configuration);
                    const uidsOfTimelineRowEntity = ObjectHelper.getAll<string>(issue, `${configuration.pathToEntity}.${configuration.pathToUid}`);

                    for (const rowUid of uidsOfTimelineRowEntity) {
                        const id = `${prefix}:${rowUid}`;

                        if (!issuesByRowAndDay.has(id)) {
                            issuesByRowAndDay.set(id, new Map<string, PayloadOfDayAndRow>());
                        }

                        const issuesOfDayOfSingleRow = issuesByRowAndDay.get(id)!;
                        if (!issuesByRowAndDay.has(issueUid)) {
                            const payload: PayloadOfDayAndRow = {
                                issue,
                                isRelevantForRow: false,
                            }
                            if (belongsToRoot(issue.type, ISSUE_TYPE_MILESTONE) && timelineRowHeader.entity?.uid === issue.project?.uid) {
                                const diffDaysStart = (issue.timeRange.start!.diff(DateTime.utc().startOf(`day`))).as(`day`);
                                const sorting = Math.ceil(timelineRowHeader.sorting);
                                payload.isRelevantForRow = (diffDaysStart === sorting);
                            }
                            if (!issuesOfDayOfSingleRow.has(issueUid) || !issuesOfDayOfSingleRow.get(issueUid)?.isRelevantForRow) {
                                issuesOfDayOfSingleRow.set(issueUid, payload);
                            }
                        }
                    }
                }
            }
        }

        // be triggered manually, since Map is reused all the time instead
        // of being used as "immutable". Thus lit does not get the change.
        this.requestUpdate();
    }

    protected _registerDragging(): void {

        const touchService = container.resolve(TouchService);
        touchService.register(this);
        touchService
            .channel$
            .pipe(takeUntil(this._disconnected$))
            .subscribe(data => {
                this._onPointerMoved(data);
            });

        const draggingService = container.resolve(TimelineMovingDraggingService);

        draggingService.listen<{ x: number, y: number, remainder: { x: number, y: number } }>(/^moved$/).pipe(
            takeUntil(this._disconnected$)
        ).subscribe(data => this._onPointerMoved(data.payload as DraggingCoordinates));

        // Listen to drag-mode events for pointer dragging state
        draggingService.listen(/drag-mode\.enabled/).pipe(
            takeUntil(this._disconnected$)
        ).subscribe(() => {
            this._pointerDraggingActive = true;
        });

        draggingService.listen(/drag-mode\.disabled/).pipe(
            takeUntil(this._disconnected$)
        ).subscribe(() => {
            this._pointerDraggingActive = false;
        });

        draggingService.listen(/space-key\.pressed/).pipe(
            takeUntil(this._disconnected$)
        ).subscribe(() => {
            this._viewMode = [ViewMode.DRAGGING, ...this._viewMode];

            // closes eventually visible popover
            this._inPopoverVisibleIssueElement = undefined;
            UiHelper.popover(null);
        })
        draggingService.listen(/space-key\.released/).pipe(
            takeUntil(this._disconnected$)
        ).subscribe(() => {
            const idx = this._viewMode.findIndex(viewMode => viewMode === ViewMode.DRAGGING);
            this._viewMode = [...this._viewMode.slice(0, idx), ...this._viewMode.slice(idx + 1)];
        })
        draggingService.register(this, Math.max(this._cellHeight, this._cellWidth));
    }

    /**
     * @deprecated Use _refreshTimelineByIntervals() instead for better IntervalLoadingGuard integration.
     * This method bypasses the cache and loads the entire visible interval.
     */
    protected async _loadAndClassifyIssues(): Promise<void> {
        const issuesByDay = await this._loadIssues();
        this._classifyAndAssignIssuesToCells(issuesByDay);
    }

    protected async _refreshTimelineByIntervals(intervals: Interval[]): Promise<void> {
        if (this._timelineConfigurations.filter(configuration => configuration.customIssueQuery).length > 0) {
            return;
        }

        const timelineBackendConnector = container.resolve(TimelineBackendConnector);
        intervals = Interval.merge(intervals);
        const intervalLoadingGuard = container.resolve(IntervalLoadingGuard);
        for (const interval of intervals) {
            intervalLoadingGuard.removeRange(interval);
            timelineBackendConnector.clearIntervalCache(interval);
            const intersection = this.interval.intersection(interval);
            if (!!intersection) {
                const issues = await timelineBackendConnector.fetchIssueOfInterval(intersection);
                for (const dateAsInterval of intersection.splitBy({day: 1})) {
                    this._timelineCells.delete(dateAsInterval.start!.toUnixInteger());
                }
                this._classifyAndAssignIssuesToCells(issues);
            }
        }
    }

    protected _renderTimeCells(): unknown {

        const start = this.interval.start!;
        const keys = Array.from(Array(this._calendarViewCalculator!.days.length).keys()).map(day => start.plus({day}).toUnixInteger());

        const today = DateTime.utc().startOf(`day`);

        return map(keys, (key, relativeColumnIndex) => {

            const rowsAtDay = (!this._timelineCells.has(key))
                ? new Map<string, Map<string, PayloadOfDayAndRow>>()
                : this._timelineCells.get(key)!;

            const date = DateTime.fromSeconds(key, {zone: `utc`}).startOf(`day`)
            const isToday = date.equals(today);

            return map(
                this._timelineRowHeadersInView,
                (rowHeader, relativeRowIndex): unknown => {
                    const {entity, configuration} = rowHeader;
                    const id = `${StringHelper.buildUserViewConfigurationKey(configuration)}:${rowHeader.uid}`;

                    const issues = rowsAtDay.get(id) ?? new Map<string, PayloadOfDayAndRow>;
                    return this._renderCell(
                        issues,
                        relativeRowIndex,
                        relativeColumnIndex,
                        isToday,
                        [6, 7].includes(DateTime.fromSeconds(key).startOf(`day`).weekday),
                        {
                            date,
                            entity,
                            issues,
                        },
                        rowHeader
                    )
                }
            )
        });
    }

    protected _isLastDayOfIssue(payloadOfDayAndRow: PayloadOfDayAndRow, date: DateTime): boolean {
        return payloadOfDayAndRow.issue.end.minus({day: 1}).hasSame(date, `day`)
    }

    protected _isFirstDayOfIssue(payloadOfDayAndRow: PayloadOfDayAndRow, date: DateTime): boolean {
        return payloadOfDayAndRow.issue.start.hasSame(date, `day`);
    }

    protected _renderIssue(payloadOfDayAndRow: PayloadOfDayAndRow, cellInfo: CellInfo, index: number): HTMLTemplateResult {
        const issueClassMap = {
            "issue_first-day": this._isFirstDayOfIssue(payloadOfDayAndRow, cellInfo.date),
            "issue_last-day": this._isLastDayOfIssue(payloadOfDayAndRow, cellInfo.date),
            "issue": true,
            "filtered-out": this._filteredOutIssues.includes(payloadOfDayAndRow.issue.uid),
            "is-relevant-timeframe": payloadOfDayAndRow.isRelevantForRow,
            ...(payloadOfDayAndRow.issue.status ? {[`issue__state-${StringHelper.toKebabCase(payloadOfDayAndRow.issue.status.key)}`]: true} : {}),
        }

        return html`
            <issue data-index=${index}
                   .issue=${payloadOfDayAndRow.issue}
                   class=${classMap(issueClassMap)}
                   style=${styleMap({background: payloadOfDayAndRow.issue.type.color})}
            >
                <div class="issue__shortcut">${payloadOfDayAndRow.issue.type.shortcut}</div>
                ${when(appState.isWeekView(), () => html`
                    <div class="issue__title">${payloadOfDayAndRow.issue.toString()}</div>
                `)}
            </issue>
        `
    }

    protected _renderCell(issues: Map<string, PayloadOfDayAndRow>, rowIndex: number, columnIndex: number, isToday: boolean, isWeekEnd: boolean, cellInfo: CellInfo, rowHeader: TimelineRowHeader<Entity>): unknown {
        const rowViewIndex = rowIndex + 5;

        const cellStyles = {
            gridRowStart: rowViewIndex,
            gridRowEnd: rowViewIndex,
            gridColumnStart: columnIndex + 2,
            gridColumnEnd: columnIndex + 2
        };

        const cellClasses = {
            "today": isToday,
            "weekend": isWeekEnd,
            "cell": true,
            "row__highlighted": this._highlightedRows.includes(rowHeader),
            "cell__sticky": appState.isStickyEntity(rowHeader.uid)
        }

        const issuesOfDay = (this._precompiledFilters ? Array.from(issues.values())
            .filter(payloadOfDayAndRow => {
                if (!(belongsToRoot(payloadOfDayAndRow.issue.type, ISSUE_TYPE_MILESTONE) || (!!this._precompiledFilters && this._precompiledFilters(payloadOfDayAndRow.issue)))) {
                    this._filteredOutIssues.push(payloadOfDayAndRow.issue.uid);
                }
                return true;
            }) : Array.from(issues.values()))
            .sort((payloadOfDayAndRowA, payloadOfDayAndRowB) => this._sortIssuesOfCell(payloadOfDayAndRowA, payloadOfDayAndRowB));

        return html`
            <cell class=${classMap(cellClasses)}
                  style=${styleMap(cellStyles)}
                  data-row-index=${rowIndex}
                  data-total-issues=${issuesOfDay.length}
                  data-total-issues-ending=${issuesOfDay.filter(p => p.issue.end.minus({day: 1}).hasSame(cellInfo.date, `day`)).length}
                  data-total-issue-diff=${issuesOfDay.length - issuesOfDay.filter(p => p.issue.end.minus({day: 1}).hasSame(cellInfo.date, `day`)).length}
                  @click=${(evt: MouseEvent) => this._openIssuesEditDialog(evt, issuesOfDay.map(p => p.issue), cellInfo)}
            >
                ${repeat(
                        issuesOfDay,
                        payloadOfDayAndRow => payloadOfDayAndRow.issue.uid,
                        (payloadOfDayAndRow, index) => this._renderIssue(payloadOfDayAndRow, cellInfo, index)
                )}
            </cell>
        `;
    }

    protected _sortIssuesOfCell(payloadOfDayAndRowA: PayloadOfDayAndRow, payloadOfDayAndRowB: PayloadOfDayAndRow) {
        const diff = payloadOfDayAndRowA.issue.start.valueOf() - payloadOfDayAndRowB.issue.start.valueOf();
        if (diff === 0) {
            return payloadOfDayAndRowB.issue.interval.count(`day`) - payloadOfDayAndRowA.issue.interval.count(`day`);
        }

        return diff;
    }

    protected _setColumnCursor(x: number, remainderX: number): void {

        HtmlHelper.setGlobalVar(`remainder-x`, `${remainderX ?? 0}px`);

        if (x !== 0) {
            // Update date cursor and reset interval for new calculation
            this._interval = null;
            appState.dateCursorDate = appState.dateCursorDate.minus({day: x ?? 0});

            // Calculate new interval after date change
            const newInterval = this.interval;

            // Use TimelineRequestManager with debouncing for race condition prevention
            const requestManager = container.resolve(TimelineRequestManager);

            requestManager.requestIssuesForIntervalDebounced(newInterval)
                .then(result => {
                    // Update timeline cells with new data
                    this._classifyAndAssignIssuesToCells(result.issuesByDay);
                })
                .catch(error => {
                    console.error('[Timeline] TimelineRequestManager request failed:', error);
                });
        }
    }

    protected async _onClickRowHeaderContextMenuNewIssue(entity: Entity | null): Promise<void> {
        const configuration = this._timelineConfigurations.at(0)!;
        const {pathToEntity} = configuration;
        const newIssue = await container.resolve(IssueRepository).create();
        ObjectHelper.set(newIssue, pathToEntity, entity);
        const template = await newIssue.clone();

        this._inPopoverVisibleIssueElement = undefined;
        IssueHelper.openIssuesEditDialog([newIssue], newIssue, template);
    }

    protected _onClickRowHeaderContextMenuDelete(entity: Entity): void {
        UiHelper.confirm({
            title: `In den Papierkorb verschieben`,
            content: html`
                <p>Wollen Sie wirklich «${entity.toString()}» in den Papierkorb verschieben?</p>
            `,
            confirmLabel: `Ja, jetzt löschen!`,
            cancelLabel: `Nein, abbrechen!`
        }).then(async result => {
            if (!result) {
                return;
            }
            const commandBus = container.resolve(CommandBus);
            if (!entity) {
                return;
            }
            const prototype = entity.constructor as ClassOf<Entity>;
            const commandPrototype = commandBus.getCommand<Entity>(prototype, `delete`);
            if (!commandPrototype) {
                console.error(`Es gibt kein Command für ${StringHelper.toKebabCase(prototype.name)}`);
                return;
            }
            await commandBus.dispatchCommand(new commandPrototype(entity))
        });
    }

    protected _onClickRowHeaderContextMenuPin(entityUid: string): void {
        appState.addTimelineEntityToUserView(this, entityUid);
    }

    protected _onClickRowHeaderContextMenuUnpin(entityUid: string): void {
        appState.removeTimelineEntityFromUserView(this, entityUid);
    }

    protected _onClickRowHeaderContextMenuMakeSticky(entity: Entity): void {
        const baseEntityName = entity.constructor.name;


        // Find the matching TimelineConfiguration for this entity
        const timelineConfig = this._timelineConfigurations.find(config =>
            config.baseEntity === entity.constructor
        );


        if (!timelineConfig) {
            console.warn(`[StickyEntities] No timeline config found for ${baseEntityName}`);
            return;
        }

        // Extract the serializable path information
        const configPaths = {
            pathToUid: timelineConfig.pathToUid,
            pathToTitle: timelineConfig.pathToTitle,
            pathToEntity: timelineConfig.pathToEntity,
            baseEntityAlias: timelineConfig.baseEntityAlias
        };

        appState.addStickyTimelineEntity(baseEntityName, entity.uid, configPaths);
    }

    protected _onClickRowHeaderContextMenuUnsticky(entityUid: string): void {
        appState.removeStickyTimelineEntity(entityUid);
    }

    protected _onClickRowHeaderContextMenuEdit(entity: Entity): void {
        UiHelper.openDialogEntityEdit<ICommand<Entity>>(
            entity.constructor as ClassOf<Entity>,
            entity,
            {
                closable: true,
                minimizable: false,
                maximizable: false,
                title: ``
            }
        );
    }

    /**
     * Handles Y-scrolling for views with fixed grid rows (repeat auto-fill).
     * Scrolling should stop at the end of available data, not grid rows.
     */
    protected _handleFixedGridScrolling(deltaY: number): void {
        const oldRowCursor = this._rowCursor;
        let newRowCursor = this._rowCursor + deltaY;

        // For fixed grid views: Calculate visible rows from CSS grid
        // Since _numberOfRows is 0, we need to calculate actual visible rows
        const stickyCount = this._stickyTimelineRowHeaders.length;

        // For fixed grid, we estimate visible rows from viewport height and cell height
        // This is a fallback when _numberOfRows is not properly calculated
        const estimatedVisibleRows = this._numberOfRows > 0
            ? Math.max(0, this._numberOfRows - stickyCount)
            : this._estimateVisibleRowsFromViewport();

        // Scroll should stop when we reach the end of available data
        const maxRowCursorIndex = Math.max(0, this._filteredTimelineRowHeaders.length - estimatedVisibleRows);

        // Limit scrolling to available data
        if (newRowCursor > maxRowCursorIndex) {
            newRowCursor = maxRowCursorIndex;
        }
        if (newRowCursor < 0) {
            newRowCursor = 0;
        }

        if (Math.abs(oldRowCursor - newRowCursor) !== 0) {
            this._rowCursor = newRowCursor;
            this.requestUpdate();
        }
    }

    /**
     * Handles Y-scrolling for views with auto grid rows (CSS auto).
     * If all data fits in available rows, Y-scrolling should be blocked entirely.
     */
    protected _handleAutoGridScrolling(deltaY: number): void {
        const stickyCount = this._stickyTimelineRowHeaders.length;

        // For auto-grid views: If _numberOfRows is 0, estimate from data count
        // Auto-grid views should generally not scroll if data is reasonable
        let shouldAllowScrolling = false;
        let maxRowCursorIndex = 0;

        if (this._numberOfRows > 0) {
            // Normal case: _numberOfRows is properly calculated
            const availableRowsForScrollable = Math.max(0, this._numberOfRows - stickyCount);
            shouldAllowScrolling = this._filteredTimelineRowHeaders.length > availableRowsForScrollable;
            maxRowCursorIndex = Math.max(0, this._filteredTimelineRowHeaders.length - availableRowsForScrollable);
        } else {
            // Fallback: Estimate reasonable threshold for auto-grid views
            // Auto-grid views with CSS "auto" should accommodate most data without scrolling
            const estimatedThreshold = 20; // Conservative threshold for auto-grid
            shouldAllowScrolling = this._filteredTimelineRowHeaders.length > estimatedThreshold;
            maxRowCursorIndex = Math.max(0, this._filteredTimelineRowHeaders.length - estimatedThreshold);
        }

        if (!shouldAllowScrolling) {
            return; // Block Y-scrolling when all data fits in auto-grid
        }

        // Allow scrolling with calculated limits
        const oldRowCursor = this._rowCursor;
        let newRowCursor = this._rowCursor + deltaY;

        if (newRowCursor > maxRowCursorIndex) {
            newRowCursor = maxRowCursorIndex;
        }
        if (newRowCursor < 0) {
            newRowCursor = 0;
        }

        if (Math.abs(oldRowCursor - newRowCursor) !== 0) {
            this._rowCursor = newRowCursor;
            this.requestUpdate();
        }
    }

    private _ensureSystemStickyEntities(): void {
        // Calendar Project als system-sticky hinzufügen falls nicht vorhanden
        if (!appState.isStickyEntity(CALENDAR_PROJECT_UID)) {
            appState.addSystemStickyTimelineEntity('Project', CALENDAR_PROJECT_UID, {
                pathToUid: 'uid',
                pathToTitle: 'title',
                pathToEntity: 'project',
                baseEntityAlias: 'daily'
            });
        }
    }

    private async _performCompleteHeaderLoad(): Promise<void> {
        // Load regular configurations first
        let regularTimelineRowHeaders = await container.resolve(TimelineBackendConnector).fetchRows(this._timelineConfigurations);

        // Load sticky configurations separately - only if view supports sticky entities
        let stickyTimelineRowHeaders: TimelineRowHeader<Entity>[] = [];

        if (this._supportsStickyEntities()) {
            const stickyConfigurations = this._createStickyTimelineConfigurations();
            if (stickyConfigurations.length > 0) {
                stickyTimelineRowHeaders = await container.resolve(TimelineBackendConnector).fetchRows(stickyConfigurations);
            }
        }

        // Apply transformations to both sets
        regularTimelineRowHeaders = this.onTimelineRowHeadersUpdated(regularTimelineRowHeaders);
        stickyTimelineRowHeaders = this.onTimelineRowHeadersUpdated(stickyTimelineRowHeaders);

        // Get sticky UIDs for deduplication - only if sticky entities are supported
        const stickyUids = this._supportsStickyEntities() ? appState.getStickyEntityUids() : [];

        // Remove sticky entities from regular headers to avoid duplicates
        this._filteredTimelineRowHeaders = regularTimelineRowHeaders.filter(
            row => !stickyUids.includes(row.uid)
        );

        // Use sticky headers directly (no filtering needed)
        this._stickyTimelineRowHeaders = stickyTimelineRowHeaders;

        // Sortierung auf beide Arrays anwenden
        if (typeof this.rowSortingFn === "function") {
            // noinspection TypeScriptValidateTypes
            this._stickyTimelineRowHeaders.sort(this.rowSortingFn);
            this._filteredTimelineRowHeaders.sort(this.rowSortingFn);
        }

        // Finale Row Headers = sticky + filtered
        this._timelineRowHeaders = [
            ...this._stickyTimelineRowHeaders,
            ...this._filteredTimelineRowHeaders
        ];

        // Process pending issues if any were deferred due to missing headers
        if (this._pendingIssuesForClassification) {
            this._classifyAndAssignIssuesToCells(this._pendingIssuesForClassification);
            this._pendingIssuesForClassification = undefined;
        }

        // Clear promise only after ALL processing is complete
        this._loadingTimelineRowHeadersPromise = null;
    }

    private _onClickUnsticky(evt: MouseEvent, uid: string): void {
        evt.preventDefault();
        evt.stopPropagation();
        appState.removeStickyTimelineEntity(uid);
    }

    private _createEntity<S extends Entity>(prototype: ClassOf<S>): void {

        const repository = container.resolve(EntityManager).getRepositoryForEntity(prototype);
        repository?.create().then(entity => {
            UiHelper.openDialogEntityEdit<AbstractCommand<S>>(
                prototype,
                entity,
                {
                    creationMode: true,
                    closable: true
                }
            );
        });
    }

    private async _loadIssues(): Promise<IssueMatrix> {
        const timelineBackendConnector = container.resolve(TimelineBackendConnector);
        return timelineBackendConnector.fetchIssueOfInterval(this.interval);
    }

    private async _onEntityChangedOrCreated(): Promise<void> {
        await this._loadTimelineRowHeaders();
    }

    private async _onIssueTypeChanged(messages: Array<BaseMessage<EntityChangedPayload<IssueType>>>): Promise<void> {
        const issueTypeUids = messages.map(m => m.payload.uid)
        const intervals = Interval.merge(IssueHelper
            .findInTimelineCellsByIssueTypeInInterval(this._timelineCells, this.interval)
            .filter(i => issueTypeUids.includes(i.type.uid))
            .map(i => i.timeRange)
        );
        this._refreshTimelineByIntervals(intervals).then();
    }

    private _onMouseOverGrid(evt: MouseEvent): void {
        if (!this._viewInDefaultMode) {
            return;
        }

        const issueElement = (evt.composedPath() as Array<HTMLElement & {
            issue: Issue
        }>).filter(element => element instanceof HTMLElement).find(element => element.tagName.toLowerCase() === `issue`);
        const isSameIssueTarget: boolean = this._inPopoverVisibleIssueElement === issueElement;

        // this._lastMouseOverEventCellTarget = newEventCellTarget;
        this._inPopoverVisibleIssueElement = issueElement;

        if (isSameIssueTarget) {
            return;
        }

        if (!issueElement) {
            this._inPopoverVisibleIssueElement = undefined;
            UiHelper.popover(null);
            return;
        }

        this._inPopoverVisibleIssueElement = issueElement;
        UiHelper.popover(html`
            <ha-issue-preview .issue=${issueElement.issue}></ha-issue-preview>
        `);
    }

    private async _onTimelineHeaderEntityDeleted(/*messages$: Array<BaseMessage<EntityDeletedPayload<Project>>>*/): Promise<void> {
        await this._loadTimelineRowHeaders();
    }

    private async _openIssuesEditDialog(evt: MouseEvent, issuesList: Issue[], cellInfo: CellInfo): Promise<void> {
        let issue = (evt.composedPath() as Array<HTMLElement & { issue: Issue }>)
            .find(element => Reflect.has(element, 'issue'))?.issue ?? null;

        const template = await container.resolve(IssueRepository).create();
        template.timeRange = Interval.fromDateTimes(
            cellInfo.date,
            cellInfo.date.plus({day: 1})
        );

        ObjectHelper.set(template, ArrayHelper.first(this._timelineConfigurations)!.pathToEntity, cellInfo.entity);

        const executionVerificationRequests = issuesList.map(entity => (<ExecutionVerification>{
            entity,
            commandName: CommandName.CREATE_ISSUE,
            canExecute: false
        }));

        const executionVerifications = await this._authorizationService.canExecute(executionVerificationRequests);

        if (evt.ctrlKey && this._authorizationService.hasPrivilege(CommandName.CREATE_ISSUE)) {
            issue = await template.clone();
            issuesList.push(issue);
        }

        if (issuesList.length < 1) {
            return;
        }

        this._inPopoverVisibleIssueElement = undefined;

        IssueHelper.openIssuesEditDialog(
            issuesList,
            issue,
            template
        );
    }

    /**
     * Mirrors AbstractStandardTimelineView's _applyActivityFilterAndPredicate +
     * _fetchMatchedUuids (7.5) for this older base class's single call site.
     * _matchedUuids is reset to null FIRST when the activity filter (changedWithin) is
     * active — a stale set from a DIFFERENT prior selection would be a worse error than
     * the accepted "nothing grayed out yet" window, and precompileFilters must see the
     * reset value, not the old one, when building this predicate.
     */
    private _precompileFilters(filters: FiltersDTO): void {
        this._matchedUuids = null;
        if (FilterHelper.isActivityFilterActive(filters)) {
            this._fetchMatchedUuids(filters);
        }
        this._precompiledFilters = FilterHelper.precompileFilters(filters, this._matchedUuids);
    }

    /**
     * Fires the timeline's activity-filter uid-set fetch and, if it resolves without
     * being superseded, stores the result and rebuilds the predicate a SECOND time.
     * listMode 'all' (not the default 'tasks') so absences/holidays/past timeframes are
     * not excluded from the uid set — the (negated) predicate would otherwise treat
     * them as permanent non-matches for as long as the filter stays active (7.5). The
     * two filter keys go through FilterHelper.asQueryParams like every other filter
     * (7.1: exactly one place translates them), not built into QueryParams by hand.
     */
    private _fetchMatchedUuids(filters: FiltersDTO): void {
        const generation = ++this._matchedUuidsFetchGeneration;
        container.resolve(IssueRepository)
            .findAllUuidsByStatus(FilterHelper.asQueryParams(FilterHelper.activityFilterOnly(filters)), null, 'all')
            .then(items => {
                if (generation !== this._matchedUuidsFetchGeneration) {
                    return; // superseded by a newer selection's fetch — discard
                }
                this._matchedUuids = new Set(items.map(item => item.uid));
                this._precompiledFilters = FilterHelper.precompileFilters(filters, this._matchedUuids);
            });
    }

    /**
     * Create layout configuration for timeline layout service
     */
    private _createLayoutConfig(): TimelineLayoutConfig {
        return {
            parentElement: this,
            cellWidthMultiplication: appState.cellWidthMultiplication,
            daysBefore: this.daysBefore,
            dateCursorDate: appState.dateCursorDate
        };
    }

    private _reCalculateView(): void {

        // Use Timeline Layout Service for complete recalculation
        const recalculationResult = this._timelineLayoutService.recalculateForResize(this._createLayoutConfig());

        this._layout = recalculationResult.layout;

        if (recalculationResult.hasChanged) {

            // Update internal properties based on new layout
            this._numberOfRows = this._layout.numberOfRows;
            this.daysAfter = this._layout.daysAfter;
            this._cellHeight = this._layout.cellHeight;
            this._cellWidth = this._layout.cellWidth;

            // Adjust row cursor to ensure full screen utilization after resize
            this._rowCursor = this._adjustRowCursorForFullScreen(this._rowCursor);

            // Reset interval to force recalculation
            this.interval = null;

            // Refresh timeline content with new intervals
            const missingIntervals = container.resolve(IntervalLoadingGuard).findGapsInInterval(this.interval);
            this._refreshTimelineByIntervals(missingIntervals).then();

            // Request UI update
            this.requestUpdate();
        }
    }

    private _renderDay(): unknown {
        return repeat(
            this._calendarViewCalculator!.days,
            day => day.id,
            day => {

                const weekDayStyles = {
                    gridRowStart: 3,
                    gridRowEnd: 3,
                    gridColumnStart: day.id + 2,
                    gridColumnEnd: day.id + 2
                }

                const dayStyles = {
                    gridRowStart: 4,
                    gridRowEnd: 4,
                    gridColumnStart: day.id + 2,
                    gridColumnEnd: day.id + 2
                }

                const weekDayClasses = {
                    weekend: day.isWeekEnd,
                    header__weekday: true,
                    today: day.isToday
                }

                const dayClasses = {
                    weekend: day.isWeekEnd,
                    header__day: true,
                    today: day.isToday
                }

                return html`
                    <div style=${styleMap(weekDayStyles)}
                         class=${classMap(weekDayClasses)}>
                        ${day.weekDay}
                    </div>
                    <div style=${styleMap(dayStyles)}
                         class=${classMap(dayClasses)}>
                        ${day.dayOfMonth}
                    </div>
                `
            }
        );
    }

    private _renderGrid(): unknown {
        const gridCssClasses = {
            "ha-timeline-grid": true,
            "detail-view": appState.isWeekView(),
            "default-view": !appState.isWeekView(),
            "default-mode": this._viewInDefaultMode,
            "dragging-mode": this._viewInDraggingMode,
            "issue-add-mode": this._viewInIssueAddMode,
        };

        // Sticky rows count als CSS Variable setzen - nur wenn sticky entities unterstützt werden
        const stickyCount = this._supportsStickyEntities() ? this._stickyTimelineRowHeaders.length : 0;
        const gridStyles = stickyCount > 0
            ? {'--sticky-rows-count': stickyCount}
            : {};

        this._filteredOutIssues = [];

        return html`
            <div class=${classMap(gridCssClasses)}
                 style=${styleMap(gridStyles)}
                 @mouseover=${this._onMouseOverGrid}
            >
                <div class="header__background"></div>
                <div class="cell__area__background"></div>

                ${this._renderMonth()}
                ${this._renderWeek()}
                ${this._renderDay()}

                ${this._renderHeaderCells()}
                ${this._renderTimeCells()}
            </div>
        `;
    }

    private _renderMonth(): unknown {
        return repeat(
            this._calendarViewCalculator!.months,
            month => month.id,
            month => {
                const monthStyles = {
                    gridRowStart: 1,
                    gridRowEnd: 1,
                    gridColumnStart: month.index.start + 2,
                    gridColumnEnd: month.index.end + 2
                };

                return html`
                    <div class="header__month" style=${styleMap(monthStyles)}>${month.name} ${month.year}</div>
                `
            }
        );
    }

    private _renderWeek(): unknown {
        return repeat(
            this._calendarViewCalculator!.weeks,
            week => week.id,
            week => {
                const weekStyles = {
                    gridRowStart: 2,
                    gridRowEnd: 2,
                    gridColumnStart: week.index.start + 2,
                    gridColumnEnd: week.index.end + 2,

                }

                return html`
                    <div class="header__week" style=${styleMap(weekStyles)}>KW ${week.num}</div>
                `
            }
        );
    }

    /**
     * Adjusts the row cursor to ensure the screen is fully filled with rows.
     * If the initial cursor position doesn't have enough rows after it,
     * move the cursor up so the last row aligns with the bottom of the screen.
     */
    private _adjustRowCursorForFullScreen(initialCursor: number): number {
        // Get the number of visible rows (excluding sticky rows)
        const stickyCount = this._stickyTimelineRowHeaders.length;
        const visibleRows = Math.max(0, this._numberOfRows - stickyCount);

        // Total available scrollable rows
        const totalScrollableRows = this._filteredTimelineRowHeaders.length;

        // If we don't have enough rows to fill the screen, start at 0
        if (totalScrollableRows <= visibleRows) {
            return 0;
        }

        // Calculate max cursor position (last row should be at bottom)
        const maxCursor = Math.max(0, totalScrollableRows - visibleRows);

        // Adjust cursor if it's too far down
        if (initialCursor > maxCursor) {
            return maxCursor;
        }

        return initialCursor;
    }

    /**
     * Fallback method to estimate visible rows when _numberOfRows is not properly calculated.
     * Used for fixed grid views where CSS determines the actual number of visible rows.
     */
    private _estimateVisibleRowsFromViewport(): number {
        // Try to get the actual grid element and calculate visible rows
        const gridElement = this.querySelector('.ha-timeline-grid');
        if (gridElement && this._cellHeight > 0) {
            const gridRect = gridElement.getBoundingClientRect();
            const headerHeight = 4 * 40; // 4 header rows * approximately 40px each
            const availableHeight = gridRect.height - headerHeight;
            const estimatedRows = Math.floor(availableHeight / this._cellHeight);
            return Math.max(1, estimatedRows); // At least 1 row visible
        }

        // Conservative fallback
        return 10;
    }

    private _onPointerMoved(data: DraggingCoordinates): void {
        HtmlHelper.setGlobalVar(`remainder-y`, `${data.remainder.y ?? 0}px`);

        // X-Scrolling (time axis) - always handled
        this._setColumnCursor(data.x, data.remainder.x);

        // Y-Scrolling (rows) - Early Return Pattern with view-specific handling
        if (data.y === 0 || data.y === undefined) {
            return;
        }

        if (this._usesFixedGridRows()) {
            this._handleFixedGridScrolling(data.y);
        } else {
            this._handleAutoGridScrolling(data.y);
        }
    }
}